import { useMarkets } from '@api/useMarkets'
import { addCommasToNumber } from '@gravitate-js/excalibrr'
import { getCurrentMarket } from '@utils/general'
import { useMemo } from 'react'

export function formatDecimal(value: number, precision = 2, useCommas = true): string {
  const fmt = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: useCommas,
  })
  return fmt.format(value)
}

const fallbackUnitProfile = {
  name: 'default',
  is_default: true,
  volume: {
    name: 'Gallons',
    short_name: 'Gal',
    symbol: null,
  },
  distance: {
    name: 'Miles',
    short_name: 'Mi',
    symbol: null,
  },
  currency: {
    name: 'Dollars',
    short_name: 'USD',
    symbol: '$',
  },
} as const

type useFormatterProps = {
  marketOverride?: string
}

export const useFormatter = (props?: useFormatterProps) => {
  const { useMarketsQuery } = useMarkets()
  const { data: marketList } = useMarketsQuery()

  const marketUnitProfile = useMemo(() => {
    const currentMarket = getCurrentMarket()
    const usedMarket = props?.marketOverride || currentMarket
    const unitProfile = marketList?.rows.find((market) => market.name === usedMarket)?.profile
    const defaultUnitProfile = marketList?.rows.find((market) => market.profile?.is_default)?.profile
    return unitProfile || defaultUnitProfile || fallbackUnitProfile
  }, [marketList])

  function formatCurrency(value: number | null, useSymbol: boolean, precision = 0): string {
    if (marketList) {
      const {
        currency: { short_name = '', symbol = '' },
      } = marketUnitProfile

      if (value || value === 0) {
        return `${useSymbol ? symbol : short_name}${addCommasToNumber(value, precision)}`
      }
      return useSymbol ? symbol : short_name
    }
    return ''
  }

  function formatDistance(value: number | null, useShortName: boolean) {
    if (marketList) {
      const {
        distance: { name = '', short_name = '' },
      } = marketUnitProfile

      if (value || value === 0) {
        return `${addCommasToNumber(value, 0)} ${useShortName ? short_name : name}`
      }
      return useShortName ? short_name : name
    }
    return ''
  }

  function formatVolume(value: number | null, useShortName: boolean) {
    if (marketList) {
      const {
        volume: { name = '', short_name = '' },
      } = marketUnitProfile

      if (value || value === 0) {
        return `${addCommasToNumber(value, 0)} ${useShortName ? short_name : name}`
      }
      return useShortName ? short_name : name
    }
    return ''
  }

  return {
    formatCurrency,
    formatDistance,
    formatVolume,
    formatDecimal,
  }
}
